import {runsInEmbeddedMode} from './embeddedMode'

/**
 * Meta Pixel (formerly Facebook Pixel), ticket GW-196.
 *
 * Loaded only after the user allowed marketing cookies in the CookieLayer. Without consent
 * neither fbevents.js is loaded nor any cookie is set. Stays off inside the native app
 * (embedded mode). VUE_APP_META_PIXEL_ID empty (local/stage) = disabled.
 */

const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js'
const PIXEL_COOKIES = ['_fbp', '_fbc']

let enabled = false
let initialized = false
let routerReady = false
let lastPageViewUrl = null

// Tracking must never break the app (e.g. window.fbq replaced or removed by a privacy extension).
function fbq(...args) {
    try {
        if (window.fbq) {
            window.fbq(...args)
        }
    } catch (error) {
        console.error('[Meta Pixel]', error)
    }
}

// Transcription of Meta's official base code: a stub that queues calls until fbevents.js has loaded.
function ensureFbq() {
    if (window.fbq) {
        return
    }

    const stub = (...args) => {
        if (stub.callMethod) {
            stub.callMethod(...args)
        } else {
            stub.queue.push(args)
        }
    }
    stub.queue = []
    stub.loaded = true
    stub.version = '2.0'
    stub.push = stub
    window.fbq = stub
    window._fbq = stub

    const script = document.createElement('script')
    script.async = true
    script.src = SCRIPT_SRC
    document.head.appendChild(script)
}

function currentUrl() {
    return window.location.pathname + window.location.search
}

// Meta sets _fbp/_fbc on the registrable domain (".gourmetta.de"): delete the host-only and the domain variant.
function deleteCookie(name) {
    const expired = `${name}=; Max-Age=0; path=/`
    document.cookie = expired
    const labels = window.location.hostname.split('.')
    if (labels.length >= 2) {
        document.cookie = `${expired}; domain=.${labels.slice(-2).join('.')}`
    }
}

// Called from main.js once the router finished its initial navigation. Before that, the first
// PageView is left to router.afterEach so a redirect (e.g. / → /speiseplan) is not counted twice.
export function markMetaPixelRouterReady() {
    routerReady = true
}

// On granted consent: load the pixel (once) and, once the router is ready, send the PageView.
export function grantMetaPixelConsent() {
    const pixelId = process.env.VUE_APP_META_PIXEL_ID
    // the cookie layer may save the same choice again; do not re-init or re-count the page
    if (!pixelId || runsInEmbeddedMode() || enabled) {
        return
    }

    try {
        ensureFbq()
    } catch (error) {
        console.error('[Meta Pixel]', error)
        return
    }

    enabled = true
    if (initialized) {
        // consent was withdrawn and granted again within this session
        fbq('consent', 'grant')
    } else {
        fbq('init', pixelId)
        initialized = true
    }
    if (routerReady) {
        lastPageViewUrl = currentUrl()
        fbq('track', 'PageView')
    }
}

// On withdrawn consent: stop tracking and remove the pixel's cookies.
export function revokeMetaPixelConsent() {
    if (!enabled) {
        return
    }
    enabled = false
    lastPageViewUrl = null
    fbq('consent', 'revoke')
    PIXEL_COOKIES.forEach(deleteCookie)
}

// PageView on route changes (router.afterEach). The url grantMetaPixelConsent() has just reported is not counted twice.
export function trackMetaPixelPageView() {
    if (!enabled) {
        return
    }
    const url = currentUrl()
    if (url === lastPageViewUrl) {
        return
    }
    lastPageViewUrl = url
    fbq('track', 'PageView')
}

// Meta standard event, e.g. 'CompleteRegistration'.
export function trackMetaPixelEvent(eventName) {
    if (!enabled) {
        return
    }
    fbq('track', eventName)
}

// Custom event, e.g. 'Login'.
export function trackMetaPixelCustomEvent(eventName) {
    if (!enabled) {
        return
    }
    fbq('trackCustom', eventName)
}
